import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { CacheKey } from "@/modules/cache/constants";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export type CalendarOnboardingPhase =
  | "not-started"
  | "oauth-started"
  | "connection-pending"
  | "completed";

export type CalendarOnboardingProvider = "google" | "outlook";

export interface PendingCalendarConnection {
  provider: CalendarOnboardingProvider;
  code: string;
  state: string | null;
}

export class CalendarOnboardingStore extends AppSubStore {
  /** Persisted phase — survives page reloads via accountCache. */
  phase: CalendarOnboardingPhase = "not-started";

  /**
   * Transient connection data — set by the OAuth callback page,
   * consumed by calendar-select-step within the same session.
   * Not persisted: if the app reloads mid-flow, we fall back to
   * showing calendar-connect again.
   */
  pendingConnection: PendingCalendarConnection | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable(this, {
      phase: observable,
      pendingConnection: observable,
      pendingCalendarConnection: computed,
      isOAuthInProgress: computed,
      shouldShow: computed,
      setPhase: action,
      setPendingConnection: action,
      reset: action,
      initialize: action,
      debugReset: action,
    });
  }

  async initialize() {
    try {
      const cached = await this.store.accountCache.get(CacheKey.CalendarOnboardingState);

      if (cached) {
        runInAction(() => {
          this.phase = cached.phase;
        });
      }
    } catch (unknownErr) {
      logger.warn({
        message: "[CalendarOnboardingStore] Failed to hydrate from accountCache",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });
    }

    // Auto-complete when a calendar is already connected.
    // Reading isConnected triggers onBecomeObserved → loadSettings() in each
    // calendar store. Once loaded, if connected, we mark completed.
    // Covers the case where user connected but never clicked "Next".
    if (this.phase !== "completed") {
      reaction(
        () => {
          const { google, outlook } = this.store.calendar;
          return {
            ready: google.isReady && outlook.isReady,
            connected: google.isConnected || outlook.isConnected,
          };
        },
        ({ ready, connected }, _prev, r) => {
          if (!ready) return;
          r.dispose();
          if (connected) {
            this.setPhase("completed");
          }
        },
        { fireImmediately: true }
      );
    }
  }

  /**
   * Alias for reading the transient pending connection.
   * Returns the connection details when phase is connection-pending
   * and transient data is available, null otherwise.
   */
  get pendingCalendarConnection(): PendingCalendarConnection | null {
    if (this.phase !== "connection-pending") return null;
    return this.pendingConnection;
  }

  get isOAuthInProgress(): boolean {
    return this.phase === "oauth-started";
  }

  /**
   * Whether to show the calendar onboarding step.
   *
   * Returns true when:
   * - Phase is oauth-started or connection-pending (mid-flow)
   * - Phase is not-started AND calendar stores have loaded AND no calendar is connected
   *
   * Returns false when:
   * - Phase is completed
   * - Phase is not-started but user already has a calendar connected
   * - Phase is not-started but calendar stores haven't loaded yet (default to hidden
   *   to avoid flashing the step for users who already connected)
   */
  get shouldShow(): boolean {
    if (this.phase === "completed") return false;
    if (this.phase === "oauth-started") return true;
    if (this.phase === "connection-pending" && this.pendingConnection) return true;

    // not-started: wait until calendar stores have loaded before deciding.
    // Default to hidden so users who already connected don't see a flash.
    const { google, outlook } = this.store.calendar;
    if (!google.isReady || !outlook.isReady) return false;
    if (google.isConnected || outlook.isConnected) return false;

    return true;
  }

  /** Persist a phase change to accountCache. */
  setPhase(nextPhase: CalendarOnboardingPhase) {
    runInAction(() => {
      this.phase = nextPhase;
    });
    this.store.accountCache.set(CacheKey.CalendarOnboardingState, { phase: nextPhase });
  }

  /** Store transient OAuth connection data (not persisted). */
  setPendingConnection(connection: PendingCalendarConnection) {
    this.pendingConnection = connection;
    this.setPhase("connection-pending");
  }

  reset() {
    runInAction(() => {
      this.pendingConnection = null;
    });
    this.setPhase("not-started");
  }

  /** Disconnect all connected calendars, then reset the onboarding flow. */
  async debugReset() {
    const { google, outlook } = this.store.calendar;

    // Ensure calendar settings are loaded so accountsList is populated.
    // loadSettings() is normally triggered lazily via onBecomeObserved on
    // isConnected, so it may never have fired if the user hasn't visited
    // the integrations page.
    await Promise.all([google.loadSettings(), outlook.loadSettings()]);

    for (const account of google.accountsList) {
      if (account.isConnected && account.googleSubjectId) {
        await google.disconnect(account.googleSubjectId);
      }
    }

    for (const account of outlook.accountsList) {
      if (account.isConnected && account.outlookSubjectId) {
        await outlook.disconnect(account.outlookSubjectId);
      }
    }

    this.reset();
  }
}
